import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Popconfirm, Input, Flex, message, Dropdown, Modal, Form, InputNumber, Select, DatePicker, Tooltip } from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  MoreOutlined,
  FilePdfOutlined,
  EyeOutlined,
  LinkOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as actions from "store/actions/AuthActions";
import { useDispatch } from "react-redux";
import { downloadReceipt } from "store/slices/PaymentSlice";
import dayjs from "dayjs";
import SendReceiptDrawer from "pages/Payments/SendReceiptDrawer";
import FilterBar, { FilterField } from "components/Common/FilterBar";
import useUrlFilters from "hooks/useUrlFilters";
import axios from "axios";
import { formatMoney, currencyPrefix, currencyDecimals } from "utils/money";
import { copyText } from "utils/clipboard";

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();
  const colors = {
    success: "#16A34A",
    completed: "#16A34A",
    paid: "#16A34A",
    pending: "#F97316",
    processing: "#3B82F6",
    failed: "#DC2626",
    cancelled: "#6B7280",
    refunded: "#8B5CF6",
  };
  const color = colors[statusLower] || "#6B7280";
  return { backgroundColor: `${color}20`, color: color };
};

// A payment link has no name of its own — fall back through the fields that
// identify it, ending at a short form of its token.
const paymentLinkLabel = (link) => {
  if (!link) return "";
  return (
    link.description
    || link.customer_name
    || (link.amount != null ? formatMoney(link.amount, link.currency_code || "INR") : "")
    || `Link ${String(link.token || link.id).slice(0, 8)}`
  );
};

const STATUS_BADGE_BASE = {
  padding: "4px 12px",
  borderRadius: "9999px",
  fontSize: "12px",
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const getItemsForDropDown = (record, navigate, handleDelete, handleViewReceipt, handleDownloadReceipt) => ([
  {
    key: '1',
    label: (
      <a href={`/payments/edit/${record.id}/`} style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
        <EditOutlined style={{ marginRight: '8px' }} />
        Edit
      </a>
    ),
  },
  {
    key: '2',
    label: (
      <a
        style={{ display: 'flex', alignItems: 'center' }}
        onClick={(e) => { e.stopPropagation(); handleViewReceipt(record); }}
      >
        <EyeOutlined style={{ marginRight: '8px' }} />
        View Receipt
      </a>
    ),
  },
  {
    key: '3',
    label: (
      <a
        style={{ display: 'flex', alignItems: 'center' }}
        onClick={(e) => { e.stopPropagation(); handleDownloadReceipt(record.id, record.receipt_no); }}
      >
        <FilePdfOutlined style={{ marginRight: '8px' }} />
        Download Receipt
      </a>
    ),
  },
  {
    key: '4',
    label: record.invoice ? (
      <a href={`/invoices/edit/${record.invoice}/`} style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
        <span style={{ marginRight: '8px' }}>📄</span>
        View Invoice
      </a>
    ) : (
      <a
        href={`/invoices/new?payment_id=${record.id}&account_id=${record.account || ''}&amount=${record.amount || ''}`}
        style={{ display: 'flex', alignItems: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ marginRight: '8px' }}>📄</span>
        Generate Invoice
      </a>
    ),
  },
  {
    type: 'divider',
  },
  {
    key: '5',
    label: (
      <Popconfirm
        title="Sure to delete?"
        onConfirm={() => handleDelete(record.id)}
        onClick={(e) => e.stopPropagation()}
      >
        <a style={{ display: 'flex', alignItems: 'center', color: '#ff4d4f' }} onClick={(e) => e.stopPropagation()}>
          <DeleteOutlined style={{ marginRight: '8px' }} />
          Delete
        </a>
      </Popconfirm>
    ),
  },
]);

// Search, filters and the page live in the URL so the view can be shared as a
// link. payment_link / has_payment_link are also what "Go to Payments" on the
// payment links page sends.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 50 },
  dateRange: { type: "dateRange", keys: ["start_date", "end_date"] },
  paymentLink: { type: "string", key: "payment_link" },
  hasPaymentLink: { type: "string", key: "has_payment_link" },
};

const PaymentsTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter("search");
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
  const paymentLinkFilter = filters.paymentLink;
  const hasPaymentLinkFilter = filters.hasPaymentLink;

  // Options for the payment-link filter dropdown
  const [paymentLinkOptions, setPaymentLinkOptions] = useState([]);
  const [selectedLinkOption, setSelectedLinkOption] = useState(null);

  const dateRange = filters.dateRange;

  // Server-side pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter("page");
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter("pageSize");
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProductTrans, setLoadingProductTrans] = useState(false);
  const [loadingTesonTrans, setLoadingTesonTrans] = useState(false);

  // State for receipt drawer
  const [receiptDrawerOpen, setReceiptDrawerOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // State for payment link modal
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [paymentLinkResult, setPaymentLinkResult] = useState(null);
  const [paymentLinkForm] = Form.useForm();
  const [linkCurrencies, setLinkCurrencies] = useState([]);
  const linkCurrencyId = Form.useWatch("currency", paymentLinkForm);
  const linkCurrencyIso =
    linkCurrencies.find((c) => c.id === linkCurrencyId)?.iso_code || "INR";
  const loadLinkCurrencies = useCallback(async () => {
    try {
      const response = await axios.get("/masters/currencies/");
      const list = response.data?.results || response.data || [];
      setLinkCurrencies(list);
      const inr = list.find((c) => c.iso_code === "INR");
      if (inr && !paymentLinkForm.getFieldValue("currency")) {
        paymentLinkForm.setFieldsValue({ currency: inr.id });
      }
    } catch (e) {
      console.error("Error loading currencies:", e);
    }
  }, [paymentLinkForm]);
  const [accountOptions, setAccountOptions] = useState([]);

  const fetchPayments = useCallback(async (page, size, search, paymentLink, range, hasPaymentLink) => {
    setLoading(true);
    try {
      const response = await actions.loadAllPayments(search, page, size, paymentLink, range, hasPaymentLink);
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error("Error fetching payments:", e);
    }
    setLoading(false);
  }, []);

  // Load payment links for the filter dropdown
  const loadPaymentLinkOptions = useCallback(async (searchVal = "") => {
    try {
      const response = await actions.loadPaymentLinks(searchVal, 1, 50);
      if (response.status === 200) {
        const rows = response.data.results || response.data || [];
        setPaymentLinkOptions(rows.map((l) => ({
          label: paymentLinkLabel(l),
          value: l.id,
        })));
      }
    } catch (e) {
      console.error("Error loading payment links:", e);
    }
  }, []);

  useEffect(() => {
    loadPaymentLinkOptions();
  }, [loadPaymentLinkOptions]);

  // A link id arriving from the URL (e.g. "Go to Payments") may not be on the
  // first page of options — fetch that one link so the Select shows its name
  // instead of a raw UUID. Held separately from the searchable option list so
  // typing in the Select can't drop the currently selected link.
  useEffect(() => {
    if (!paymentLinkFilter) {
      setSelectedLinkOption(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await actions.loadPaymentLink(paymentLinkFilter);
        if (!cancelled && response.status === 200) {
          setSelectedLinkOption({ label: paymentLinkLabel(response.data), value: response.data.id });
        }
      } catch (e) {
        console.error("Error loading payment link:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [paymentLinkFilter]);

  // Searchable options, with the selected link pinned on top when it isn't in them
  const paymentLinkSelectOptions = useMemo(() => (
    selectedLinkOption && !paymentLinkOptions.some((o) => o.value === selectedLinkOption.value)
      ? [selectedLinkOption, ...paymentLinkOptions]
      : paymentLinkOptions
  ), [selectedLinkOption, paymentLinkOptions]);

  // Memoized: the PAYMENT LINK column calls this, and the columns are memoized too.
  const onPaymentLinkFilterChange = useCallback((value) => {
    setFilters({ paymentLink: value, page: 1 });
  }, [setFilters]);

  const onHasPaymentLinkFilterChange = (value) => {
    setFilters({ hasPaymentLink: value, page: 1 });
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    }
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const onDateRangeChange = (dates) => {
    setFilters({ dateRange: dates, page: 1 }); // Reset to first page on new filter
  };

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (dateRange && dateRange[0] && dateRange[1] ? 1 : 0) +
    (paymentLinkFilter ? 1 : 0) +
    (hasPaymentLinkFilter ? 1 : 0);

  const clearFilters = () => {
    setFilters({ dateRange: null, paymentLink: null, hasPaymentLink: null, page: 1 });
  };

  // Fetch payments when page, pageSize, search, or any filter changes
  useEffect(() => {
    fetchPayments(currentPage, pageSize, searchTerm, paymentLinkFilter, dateRange, hasPaymentLinkFilter);
  }, [currentPage, pageSize, searchTerm, paymentLinkFilter, hasPaymentLinkFilter, dateRange, fetchPayments]);

  const handleDelete = useCallback(async (id) => {
    const response = await actions.deletePayments(id);
    if (response.status === 204) {
      fetchPayments(currentPage, pageSize, searchTerm, paymentLinkFilter, dateRange, hasPaymentLinkFilter);
    }
  }, [currentPage, pageSize, searchTerm, paymentLinkFilter, hasPaymentLinkFilter, dateRange, fetchPayments]);

  // View receipt in drawer
  const handleViewReceipt = useCallback((record) => {
    setSelectedPayment(record);
    setReceiptDrawerOpen(true);
  }, []);

  // Close receipt drawer
  const handleReceiptDrawerClose = useCallback(() => {
    setReceiptDrawerOpen(false);
    setSelectedPayment(null);
  }, []);

  // Download receipt
  const handleDownloadReceipt = useCallback(async (id, receiptNo) => {
    try {
      message.loading({ content: 'Downloading receipt...', key: 'downloadReceipt' });
      await dispatch(downloadReceipt({ id, receiptNo })).unwrap();
      message.success({ content: 'Receipt downloaded!', key: 'downloadReceipt' });
    } catch (error) {
      message.error({ content: 'Failed to download receipt', key: 'downloadReceipt' });
    }
  }, [dispatch]);

  // Load accounts for payment link modal
  const loadAccounts = useCallback(async (search = "") => {
    try {
      const response = await actions.loadAllAccounts(search, 1, 20);
      if (response.status === 200) {
        setAccountOptions((response.data.results || response.data || []).map(a => ({
          label: a.name,
          value: a.id,
        })));
      }
    } catch (e) {
      console.error("Error loading accounts:", e);
    }
  }, []);

  const handleCreatePaymentLink = async (values) => {
    setPaymentLinkLoading(true);
    try {
      const response = await actions.createPaymentLink({
        amount: values.amount,
        ...(values.currency ? { currency: values.currency } : {}),
        description: values.description || '',
        account: values.account || null,
        customer_name: values.customer_name || '',
        customer_email: values.customer_email || '',
        customer_phone: values.customer_phone || '',
      });
      if (response.status === 201) {
        setPaymentLinkResult(response.data);
        message.success("Payment link created!");
      }
    } catch (e) {
      const data = e?.response?.data;
      const first = data && typeof data === "object" ? Object.values(data)[0] : null;
      message.error((Array.isArray(first) ? first[0] : first) || "Failed to create payment link");
      console.error(e);
    }
    setPaymentLinkLoading(false);
  };

  const handleCopyPaymentLink = () => {
    if (paymentLinkResult?.payment_link_url) {
      copyText(paymentLinkResult.payment_link_url).then((ok) => {
        if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
        message.success("Payment link copied to clipboard!");
      });
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const defaultColumns = useMemo(() => [
    {
      title: "PAYMENT ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      ellipsis: true,
      render: (id) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {id ? id.slice(0, 8) : '-'}
        </span>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: (a, b) => (a?.status || '').localeCompare(b?.status || ''),
      render: (status) => (
        <span style={{ ...getStatusStyle(status), ...STATUS_BADGE_BASE }}>
          {status}
        </span>
      ),
    },
    {
      title: "PAYMENT MODE",
      dataIndex: "payment_mode",
      key: "payment_mode",
      width: 150,
      sorter: (a, b) => (a?.payment_mode || '').localeCompare(b?.payment_mode || ''),
      render: (payment_mode) => payment_mode ? (
        <span style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: '#1890ff20',
          color: '#1890ff',
          whiteSpace: 'nowrap',
        }}>
          {payment_mode.replace(/_/g, ' ')}
        </span>
      ) : <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: "RECEIPT NO",
      dataIndex: "receipt_no",
      key: "receipt_no",
      width: 160,
      sorter: (a, b) => (a?.receipt_no || '').localeCompare(b?.receipt_no || ''),
      render: (receipt_no) => receipt_no ? (
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
          {receipt_no}
        </span>
      ) : <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: "TRANSACTION ORDER REF ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 200,
      sorter: (a, b) => (a?.transaction_id || '').localeCompare(b?.transaction_id || ''),
      render: (transaction_id, record) => (
        <a
          href={`/payments/edit/${record.id}/`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#1890ff', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {transaction_id || '-'}
        </a>
      ),
    },
    {
      title: "TRANSACTION REFERENCE",
      dataIndex: "transaction_ref",
      key: "transaction_ref",
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a?.transaction_ref || '').localeCompare(b?.transaction_ref || ''),
      render: (transaction_ref) => transaction_ref ? (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {transaction_ref}
        </span>
      ) : <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: "INVOICE",
      dataIndex: ["invoice_data", "inv_number"],
      key: "inv_number",
      width: 180,
      sorter: (a, b) =>
        (a?.invoice_data?.inv_number || '').localeCompare(b?.invoice_data?.inv_number || ''),
      render: (inv_number, record) => (
        <a
          href={`/invoices/edit/${record.invoice}/`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#1890ff', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {inv_number || '-'}
        </a>
      ),
    },
    {
      title: "PAYMENT LINK",
      dataIndex: "payment_link_data",
      key: "payment_link",
      width: 200,
      ellipsis: true,
      render: (link) => {
        if (!link) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tooltip
            title={
              <div>
                <div><strong>{paymentLinkLabel(link)}</strong></div>
                {link.amount != null && <div>Amount: {formatMoney(link.amount, link.currency_code || 'INR')}</div>}
                {link.status && <div>Status: {link.status}</div>}
                {link.customer_name && <div>Customer: {link.customer_name}</div>}
                {link.token && <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{link.token.slice(0, 12)}…</div>}
                <div style={{ marginTop: 4, opacity: 0.75 }}>Click to filter by this link</div>
              </div>
            }
          >
            <a
              onClick={(e) => { e.stopPropagation(); onPaymentLinkFilterChange(link.id); }}
              style={{ color: '#1890ff', whiteSpace: 'nowrap' }}
            >
              <LinkOutlined style={{ marginRight: 6 }} />
              {paymentLinkLabel(link)}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "ACCOUNT",
      dataIndex: "account_name",
      key: "account_name",
      width: 150,
      ellipsis: true,
      sorter: (a, b) => (a?.account_name || '').localeCompare(b?.account_name || ''),
      render: (account_name, record) => account_name || record.invoice_data?.account_name || '-',
    },
    {
      title: "CUSTOMER",
      dataIndex: "customer_name",
      key: "customer",
      width: 200,
      ellipsis: true,
      sorter: (a, b) => (a?.customer_name || '').localeCompare(b?.customer_name || ''),
      render: (customer_name, record) => {
        // Only payments collected through a link carry payer details; for the
        // rest the ACCOUNT column already says who it came from.
        if (!customer_name && !record.customer_email) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <Tooltip
            title={
              <div>
                {customer_name && <div><strong>{customer_name}</strong></div>}
                {record.customer_email && <div>{record.customer_email}</div>}
                {record.customer_phone && <div>{record.customer_phone}</div>}
              </div>
            }
          >
            <div style={{ lineHeight: 1.3 }}>
              {customer_name && (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer_name}</div>
              )}
              {record.customer_email && (
                <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {record.customer_email}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      sorter: (a, b) => (a?.amount || 0) - (b?.amount || 0),
      align: "right",
      render: (value, record) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {record.currency_code || record.invoice_data?.currency_code || 'INR'} {parseFloat(value || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: "BALANCE",
      dataIndex: ["invoice_data", "balance"],
      key: "balance",
      width: 120,
      sorter: (a, b) => (a?.invoice_data?.balance || 0) - (b?.invoice_data?.balance || 0),
      align: "right",
      render: (value, record) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {record.currency_code || record.invoice_data?.currency_code || 'INR'} {parseFloat(value || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: "EXCHANGE RATE",
      dataIndex: "exchange_rate",
      key: "exchange_rate",
      width: 110,
      align: "right",
      render: (value, record) => {
        const code = record.currency_code || record.invoice_data?.currency_code || 'INR';
        if (!value || code === 'INR') return <span style={{ color: '#999' }}>-</span>;
        return <span style={{ whiteSpace: 'nowrap' }}>{parseFloat(value).toFixed(4)}</span>;
      },
    },
    {
      title: "DATE",
      dataIndex: "created_at",
      key: "date",
      width: 120,
      sorter: (a, b) => dayjs(a?.created_at) - dayjs(b?.created_at),
      align: "right",
      render: (created_at) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '9px', color: '#888', marginRight: '4px' }}>{dayjs(created_at).format("ddd")}</span>
          <span>{dayjs(created_at).format("YYYY-MM-DD")}</span>
        </div>
      ),
    },
    {
      title: "BANK STATEMENT",
      dataIndex: "bank_transaction",
      key: "bank_transaction",
      width: 150,
      align: "center",
      sorter: (a, b) => (a?.bank_transaction ? 1 : 0) - (b?.bank_transaction ? 1 : 0),
      render: (bank_transaction, record) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: bank_transaction ? '#52c41a20' : '#8c8c8c20',
          color: bank_transaction ? '#52c41a' : '#8c8c8c'
        }}>
          {bank_transaction ? 'Attached' : 'Not Attached'}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "edit",
      key: "edit",
      width: 80,
      render: (_, record) => (
        dataSource?.length >= 1 ? (
          <Flex gap="middle" style={{ padding: 5 }}>
            <Dropdown
              menu={{ items: getItemsForDropDown(record, navigate, handleDelete, handleViewReceipt, handleDownloadReceipt) }}
              placement="bottomLeft"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </Flex>
        ) : null
      ),
    },
  ], [navigate, handleDelete, handleViewReceipt, handleDownloadReceipt, onPaymentLinkFilterChange, dataSource?.length]);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState("checkbox");


  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} payments
      </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search payments"
            activeCount={activeFilterCount}
            onClear={clearFilters}
            title="Filter Payments"
            addLabel="Add Payment"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/payments/new")}
            addClassName="add-payment-btn"
            onRefresh={() => fetchPayments(currentPage, pageSize, searchTerm, paymentLinkFilter, dateRange, hasPaymentLinkFilter)}
            refreshing={loading}
          >
            <FilterField label="Date Range">
              <DatePicker.RangePicker
                placeholder={["Start Date", "End Date"]}
                value={dateRange}
                onChange={onDateRangeChange}
                allowClear
                presets={[
                  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
                  { label: "Last 7 Days", value: [dayjs().subtract(6, "day"), dayjs()] },
                  { label: "Last 30 Days", value: [dayjs().subtract(29, "day"), dayjs()] },
                  { label: "This Month", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
                  { label: "Last Month", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
                  { label: "This Year", value: [dayjs().startOf("year"), dayjs().endOf("year")] },
                ]}
              />
            </FilterField>
            <FilterField label="Payment Link">
              <Select
                allowClear
                showSearch
                placeholder="Filter by payment link"
                value={paymentLinkFilter || undefined}
                onChange={onPaymentLinkFilterChange}
                onSearch={(val) => loadPaymentLinkOptions(val)}
                filterOption={false}
                options={paymentLinkSelectOptions}
                notFoundContent={null}
              />
            </FilterField>
            <FilterField label="Payment Link Attached">
              <Select
                allowClear
                placeholder="Payment link attached?"
                value={hasPaymentLinkFilter || undefined}
                onChange={onHasPaymentLinkFilterChange}
                options={[
                  { label: "Has payment link", value: "true" },
                  { label: "No payment link", value: "false" },
                ]}
              />
            </FilterField>
          </FilterBar>
        </div>
        <Flex
          gap="small"
          style={{
            width: "auto",
          }}
          wrap="wrap"
        >
          <Button
            // onClick={handleAdd}
            onClick={async () => {
              const response = await actions.update_payment_statuses();
              if (response.status == 200) {
                console.log(response);
              }
            }}
            icon={<SyncOutlined />}
            type="default"
            style={{
              minWidth: "120px",
              width: "auto",
            }}
            className="update-payments-btn"
          >
            Update Payments
          </Button>
          <Button
            // onClick={handleAdd}
            onClick={async () => {
              setLoadingTesonTrans(true);
              const response = await actions.update_payment_statuses();
              if (response.status == 200) {
                console.log(response);
              }
              setLoadingTesonTrans(false);
            }}
            icon={loadingTesonTrans ? <SyncOutlined spin /> : <SyncOutlined />}
            type="default"
            style={{
              minWidth: "120px",
              width: "auto",
            }}
            className="refresh-teson-btn"
          >
            Refresh Teson Transactions
          </Button>
          <Button
            // onClick={handleAdd}
            onClick={async () => {
              setLoadingProductTrans(true);
              const response = await actions.update_payment_statuses();
              if (response.status == 200) {
                console.log(response);
                message.success("Product transactions refreshed successfully!")
              }
              setLoadingProductTrans(false);
            }}
            icon={
              loadingProductTrans ? <SyncOutlined spin /> : <SyncOutlined />
            }
            type="default"
            style={{
              minWidth: "120px",
              width: "auto",
            }}
            className="refresh-product-btn"
          >
            Refresh Product Transactions
          </Button>
          <Button
            onClick={() => {
              setPaymentLinkResult(null);
              paymentLinkForm.resetFields();
              loadAccounts();
              loadLinkCurrencies();
              setPaymentLinkModalOpen(true);
            }}
            icon={<LinkOutlined />}
            type="default"
            style={{
              minWidth: "120px",
              width: "auto",
            }}
          >
            Create Payment Link
          </Button>
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          className="payments-table"
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['50', '100', '200'],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
          scroll={{
            x: 'max-content',
            y: 'calc(100vh - 350px)'
          }}
          sticky
        />
      </div>

      {/* Payment Link Modal */}
      <Modal
        title={paymentLinkResult ? "Payment Link Created" : "Create Payment Link"}
        open={paymentLinkModalOpen}
        onCancel={() => { setPaymentLinkModalOpen(false); setPaymentLinkResult(null); }}
        footer={paymentLinkResult ? [
          <Button key="close" onClick={() => { setPaymentLinkModalOpen(false); setPaymentLinkResult(null); }}>
            Close
          </Button>,
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyPaymentLink}>
            Copy Link
          </Button>,
        ] : null}
        destroyOnClose
      >
        {paymentLinkResult ? (
          <div>
            <p style={{ marginBottom: 12, color: '#52c41a', fontWeight: 500 }}>Payment link created successfully!</p>
            <Input
              value={paymentLinkResult.payment_link_url}
              readOnly
              addonAfter={
                <CopyOutlined onClick={handleCopyPaymentLink} style={{ cursor: 'pointer' }} />
              }
            />
            <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
              Amount: {formatMoney(paymentLinkResult.amount, paymentLinkResult.currency_data?.iso_code)} | Expires: {dayjs(paymentLinkResult.expires_at).format("YYYY-MM-DD HH:mm")}
            </div>
          </div>
        ) : (
          <Form
            form={paymentLinkForm}
            layout="vertical"
            onFinish={handleCreatePaymentLink}
          >
            <Form.Item name="currency" label="Currency">
              <Select
                showSearch
                optionFilterProp="label"
                options={linkCurrencies.map((c) => ({ value: c.id, label: `${c.iso_code} — ${c.currency}` }))}
              />
            </Form.Item>
            <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Amount is required' }]}>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={currencyDecimals(linkCurrencyIso)}
                placeholder="Enter amount"
                prefix={currencyPrefix(linkCurrencyIso)}
              />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input placeholder="Payment description (optional)" />
            </Form.Item>
            <Form.Item name="account" label="Account">
              <Select
                allowClear
                showSearch
                placeholder="Select account (optional)"
                filterOption={false}
                onSearch={(val) => loadAccounts(val)}
                options={accountOptions}
              />
            </Form.Item>
            <Form.Item name="customer_name" label="Customer Name">
              <Input placeholder="Customer name (optional)" />
            </Form.Item>
            <Form.Item name="customer_email" label="Customer Email">
              <Input placeholder="Customer email (optional)" type="email" />
            </Form.Item>
            <Form.Item name="customer_phone" label="Customer Phone">
              <Input placeholder="Customer phone (optional)" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={paymentLinkLoading} block>
                Create Payment Link
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Receipt Drawer */}
      <SendReceiptDrawer
        open={receiptDrawerOpen}
        onClose={handleReceiptDrawerClose}
        payment={selectedPayment}
      />
    </div>
  );
};
export default PaymentsTable;
